import React from 'react'
import {
  Switch,
  Route,
  NotFound,
  Redirect,
  useLocation,
  useQueryParams,
  AuthRoute,
  buildLangUrl,
} from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { useFeatureFlag } from '@fs/zion-flags'
import { preCachePreferences } from '@fs/zion-preferences'
import { useUser } from '@fs/zion-user'
import { targetEnv } from '@fs/zion-config'
import { i18n } from '@fs/zion-locale'
import QueryClientProviderWrapper from './QueryClientProviderWrapper'
import { legalDocRoutes } from './pages/LegalPages/legalDocs'
import AboutPageRoutes from './aboutPageRoutes'

const SiteMap = React.lazy(() => import('./pages/SiteMap'))
const SponsorshipPage = React.lazy(() => import('./pages/SponsorshipPage/SponsorshipPage'))
const BlankPage = React.lazy(() => import('./pages/BlankPage/Blank'))
const NLIHP = React.lazy(() => import('./pages/NLIHP/NLIHP'))
const LIHP = React.lazy(() => import('./pages/LIHPProject/LIHP'))
const OrdinancesProject = React.lazy(() => import('./pages/OrdinancesProject'))
const SharablePage = React.lazy(() => import('./pages/LIHPProject/pages/SharablePage'))
const InviteFriendsSharePage = React.lazy(() => import('./pages/LIHPProject/pages/InviteFriendsSharePage'))
const SinglePostViewPage = React.lazy(() => import('./pages/LIHPProject/pages/SinglePostViewPage'))
const LoadingSpinner = React.lazy(() => import('./shared/components/LoadingSpinner'))
const TreebudsPage = React.lazy(() => import('./pages/TreebudsProject/TreebudsPage'))
const TreebudsPidPage = React.lazy(() => import('./pages/TreebudsProject/TreebudsPidPage'))
const RecruitingScreener = React.lazy(() => import('./pages/RecruitingScreener'))
const MyFamily = React.lazy(() => import('./pages/MyFamilyPage'))
const LegalPages = React.lazy(() => import('./pages/LegalPages/LegalPages'))
const CookiePage = React.lazy(() => import('./pages/CookiePage'))
const LocalePage = React.lazy(() => import('./pages/LocalePage/LocalePage'))
const AccessibilityPage = React.lazy(() => import('./pages/AccessibilityPage'))
const WhatPage = React.lazy(() => import('./pages/AboutPagesProject/WhatPage'))
const HomelandsPage = React.lazy(() => import('./pages/AboutPagesProject/HomelandsPage'))
const ConnectionsPage = React.lazy(() => import('./pages/AboutPagesProject/ConnectionsPage'))
const MemoriesPage = React.lazy(() => import('./pages/AboutPagesProject/MemoriesPage'))
const PreservePage = React.lazy(() => import('./pages/AboutPagesProject/PreservePage'))
const ProvidedByPage = React.lazy(() => import('./pages/AboutPagesProject/ProvidedByPage'))
const FaqsPage = React.lazy(() => import('./pages/AboutPagesProject/FaqsPage'))
const IsItFreePage = React.lazy(() => import('./pages/AboutPagesProject/IsItFreePage'))
const DataSafePage = React.lazy(() => import('./pages/AboutPagesProject/DataSafePage'))

// Wrapper component to allow the preference-fetching hook to run *before* the app loads, so that we catch everything on initial load, instead of piecemeal.
const TopLevelAppWrapper = ({ children }) => {
  // Pre-cache the preferences commonly used in the app with a single call
  preCachePreferences(
    'help-tutorial.show-ad-prefs',
    'home.familyFeed.hideToggle',
    'home.recommendedTasks.filters.records',
    'home.recommendedTasks.filters.temple',
    'home.task.dismissedMissingInfo',
    'home.task.dismissedTreeHoles',
    'home.task.hintClosed',
    'home.task.howToStartClosed',
    'home.task.TFHCTrainingDismissed',
    'to-do-list.card-open',
    'tree.showLDSTempleInfo',
    'tree.showNamesInRomanScript',
    'match.service'
  )

  return children
}

const languagePatternString = '([a-z]{2,3}|[a-z]{2,3}-[a-zA-Z-]+)'
const homePath = '(home)?'
const localePath = `${languagePatternString}`
const matchRootPath = `/${localePath}/${homePath}`

function App() {
  const { search, hash, pathname: locationPathname } = useLocation()
  let homeRedirect = false
  const { isOn: ordinancesPageFlagOn } = useFeatureFlag('home_shared_ordinancesPage')
  const { treatment: ordinancesABTest } = useFeatureFlag('home_shared_ordinancesAB')
  const { isOn: localePageFlagOn } = useFeatureFlag('home_localePage')
  const { isOn: globalNlihp } = useFeatureFlag('home_homeReact_globalnlihp')
  const ordinancesPageTest = ordinancesABTest === 'ordinancesPage' || ordinancesABTest === 'both'
  const { isOn: aboutPagesFlagIsOn } = useFeatureFlag('home_homeReact_aboutPages')
  // Flag is owned by @fs/zion-recruiting-feedback-overlay, which invites people to the screener.
  const { isOn: recruitingFeedbackFlagIsOn } = useFeatureFlag('home_showRecruitingFeedback')
  const showScreener = recruitingFeedbackFlagIsOn && i18n.language === 'en'

  // removing /home on/languageCode/home here since we can't detect that on the server due to DTM stripping info.
  // If the url matches /${lang} or /${lang}/home/, set localeOnHome to /${lang}, otherwise it is undefined
  const localeOnHome = window.location.pathname.match(/^(\/[a-z]{2,3}|[a-z]{2,3}-[a-zA-Z-]+)(?:\/home\/)?$/)?.[1]
  if (targetEnv !== 'local' && localeOnHome) {
    // Redirect to /${lang}/ or /${lang}/global if the globalNlihp flag is on
    homeRedirect = buildLangUrl(`${localeOnHome}/${globalNlihp ? 'global' : ''}${search}${hash}`)
  }

  return (
    <ErrorBoundary>
      <TopLevelAppWrapper>
        <QueryClientProviderWrapper>
          <Switch>
            {homeRedirect && <Redirect to={homeRedirect} />}
            {globalNlihp && (
              <Route path={`/${localePath}/global`} exact>
                <HomePageRoutes />
              </Route>
            )}

            <Route path={`${matchRootPath}/sharable/invite-friends`}>
              <SharablePage sharableComponent={InviteFriendsSharePage} />
            </Route>

            <Route path={`${matchRootPath}/about/accessibility-statement`} component={AccessibilityPage} />
            {aboutPagesFlagIsOn && (
              <Route path={`${matchRootPath}/about/what-is-this-site`} exact component={WhatPage} />
            )}
            {aboutPagesFlagIsOn && (
              <Route path={`${matchRootPath}/about/who-provides-it`} exact component={ProvidedByPage} />
            )}
            {aboutPagesFlagIsOn && (
              <Route path={`${matchRootPath}/about/we-keep-your-data-safe`} exact component={DataSafePage} />
            )}
            {aboutPagesFlagIsOn && <Route path={`${matchRootPath}/about/faqs`} exact component={FaqsPage} />}
            {aboutPagesFlagIsOn && (
              <Route path={`${matchRootPath}/about/is-it-really-free`} exact component={IsItFreePage} />
            )}
            {aboutPagesFlagIsOn && (
              <Route path={`${matchRootPath}/about/`} exact>
                <AboutPageRoutes />
              </Route>
            )}
            {!aboutPagesFlagIsOn && (
              <Route path={`${matchRootPath}/about/*`}>
                <Redirect to={locationPathname.replace(/\/about\/.*/, '/about')} />
              </Route>
            )}
            {!aboutPagesFlagIsOn && (
              <Route path={`${matchRootPath}/about`} exact>
                <AboutPageRoutes />
              </Route>
            )}
            {aboutPagesFlagIsOn && (
              <Route path={`${matchRootPath}/about/benefits/homelands`} component={HomelandsPage} />
            )}
            {aboutPagesFlagIsOn && (
              <Route path={`${matchRootPath}/about/benefits/connections`} component={ConnectionsPage} />
            )}
            {aboutPagesFlagIsOn && <Route path={`${matchRootPath}/about/benefits/memories`} component={MemoriesPage} />}
            {aboutPagesFlagIsOn && <Route path={`${matchRootPath}/about/benefits/preserve`} component={PreservePage} />}
            <Route path={`${matchRootPath}/sponsorship`} component={SponsorshipPage} />
            <Route path={`${matchRootPath}/blank`} render={() => <BlankPage />} />
            <Route path={`${matchRootPath}/site-map`} component={SiteMap} />
            <Route path={`${matchRootPath}/myfamily`} component={MyFamily} />
            <Route path={`${matchRootPath}/cookies`} component={CookiePage} />
            {localePageFlagOn && <Route path={`${matchRootPath}/locale`} component={LocalePage} />}
            <Route path={`${matchRootPath}/legal/:docUrl(${legalDocRoutes.join('|')})`} component={LegalPages} />
            <AuthRoute path={`${matchRootPath}/beta/treebuds/:pid`} component={TreebudsPidPage} />
            <AuthRoute path={`${matchRootPath}/beta/treebuds`} component={TreebudsPage} />
            {showScreener && <AuthRoute path={`${matchRootPath}/screener`} component={RecruitingScreener} />}

            {(ordinancesPageFlagOn || ordinancesPageTest) && (
              <Route path={`${matchRootPath}/ordinances`} render={() => <OrdinancesProject />} />
            )}

            <Route path={matchRootPath} exact>
              <HomePageRoutes />
            </Route>
            <Route path={`${matchRootPath}/(feed|friends|tasks|recents|to-do)`}>
              <HomePageRoutes />
            </Route>

            <Route component={NotFound} />
          </Switch>
        </QueryClientProviderWrapper>
      </TopLevelAppWrapper>
    </ErrorBoundary>
  )
}

function HomePageRoutes() {
  const { query } = useQueryParams()
  const singlePost = Boolean(query.post)
  const { userLoading, signedIn } = useUser()
  const { pathname } = useLocation()
  if (userLoading) {
    return <LoadingSpinner />
  }
  if (!signedIn) {
    if (singlePost) {
      return <SinglePostViewPage publicPost unauthenticated />
    }
    if (/(feed|friends|tasks|recents|to-do)/i.test(pathname)) {
      typeof window !== 'undefined' &&
        window.location.replace(`/auth/familysearch/login?returnUrl=${encodeURIComponent(window.location.href)}`)
      return <LoadingSpinner />
    }
    return <NLIHP />
  }
  return <LIHP />
}

export default App
